/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { JSX } from 'preact';
import { classNames } from '#utils/UNSAFE_classNames';
import { BundleType } from '#resources/nls/bundle';
import { getItemAriaProps } from './tagCloudUtils';
import { styles } from './themes/TagCloudStyles.css';
import { getItemPadding } from './tagCloudNavUtils';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';

export type TagItemProps<K> = {
  id: K;
  label: string;
  accessibleLabel?: string;
  value: number;
  fontSize: number;
  role?: UnpackSignals<JSX.HTMLAttributes['role']>;
  itemIdx?: number;
  supportsSelection?: boolean;
  supportsAction?: boolean;
  translations?: BundleType;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  isHighlighted?: boolean;
  isSelected?: boolean;
  isCurrent?: boolean;
  isFocused?: boolean;
  isReadonly?: boolean;
  class?: string;
  style?: JSX.CSSProperties;
  activeId?: string;
};

const TagCloudItem = <K,>(props: TagItemProps<K>) => {
  const {
    itemBase,
    itemStyle,
    hoverUnselectedItemStyle,
    selectedItemStyle,
    hoveredSelectedItemStyle,
    dimmedItemStyle,
    interactiveCursorStyle
  } = styles;
  const isNotDimmed = props.isHighlighted || props.isHighlighted == null; // null means no highlighting feature

  const itemClasses = classNames([
    itemBase,
    props.isSelected ? selectedItemStyle : itemStyle,
    props.isCurrent
      ? props.isSelected
        ? hoveredSelectedItemStyle
        : props.isFocused || props.supportsSelection
        ? hoverUnselectedItemStyle
        : ''
      : '',
    isNotDimmed ? '' : dimmedItemStyle,
    props.supportsAction || props.supportsSelection ? interactiveCursorStyle : ''
  ]);

  const itemAriaProps = getItemAriaProps(
    !!props.supportsSelection,
    props.translations,
    props.isSelected,
    props.accessibleLabel,
    props.role
  );

  const padding = getItemPadding(props.h);

  return (
    <div
      key={props.id}
      class={itemClasses}
      data-id={`${props.id}`}
      data-idx={props.itemIdx}
      style={{
        ...props.style,
        transform: `translate(${(props.x || 0) - padding}px,${(props.y || 0) - padding}px)`,
        fontSize: props.fontSize,
        padding: `${padding}px`,
        lineHeight: props.fontSize + 'px'
      }}
      id={props.activeId}
      {...itemAriaProps}>
      {props.label}
    </div>
  );
};

export { TagCloudItem };
